// Display formatting for the analytics views. Pure; tested in format.test.ts.
// Missing values render as "—", never 0: the old page turned failed fetches into
// "0 followers" and nobody could tell a broken connection from a quiet account.

const DASH = '—';

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

export function fmtNumber(n: number | null | undefined): string {
  return isNum(n) ? Math.round(n).toLocaleString() : DASH;
}

/** 1.3K · 12.9K · 4.2M (and 980 as is) — for tiles where width is tight. */
export function fmtCompact(n: number | null | undefined): string {
  if (!isNum(n)) return DASH;
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

/** A value that is already a percentage (4.2 → "4.2%"). */
export function fmtPct(n: number | null | undefined, digits = 1): string {
  return isNum(n) ? `${n.toFixed(digits)}%` : DASH;
}

/** Percentage change between two totals; null when there is nothing to compare. */
export function pctChange(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (!isNum(current) || !isNum(previous) || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

/** Seconds as "45s", "3m 05s", "1h 02m". */
export function fmtDuration(seconds: number | null | undefined): string {
  if (!isNum(seconds)) return DASH;
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function fmtMonth(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

/** "3 h ago", "2 days ago", "just now" — for freshness labels. */
export function fmtRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const minutes = Math.round((now.getTime() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}
